import { z } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';
import { macroExecutionBundleSchema, playbackAssetGrantSchema } from '../types/api/macroExecution';
import { macroTriggerSchema } from '../types/api/macroTree';

export enum MacroBundleMessageType {
  REQUEST = 'MACROBUNDLE_REQUEST',
  RESPONSE = 'MACROBUNDLE_RESPONSE',
}
const identity = z.object({ requestId: z.string().uuid(), campaignId: z.string().min(1).max(128) });
export const macroBundleErrorSchema = z.enum(['forbidden', 'unavailable', 'busy', 'missing_artifact', 'macro_graph_migration_required']);
export type MacroBundleError = z.infer<typeof macroBundleErrorSchema>;

export const macroBundleRequestSchema = baseMessageSchema.merge(identity).extend({
  type: z.literal(MacroBundleMessageType.REQUEST), trigger: macroTriggerSchema,
}).strict();
/** Success reply: the bundle plus the grant Playjector uses to fetch its reference pages. */
export const macroBundleSuccessResponseSchema = baseMessageSchema.merge(identity).extend({
  type: z.literal(MacroBundleMessageType.RESPONSE), bundle: macroExecutionBundleSchema, assets: playbackAssetGrantSchema,
}).strict();
/**
 * Success reply for a deliberately disabled launch revision: the campaign has
 * no macro to run. This is NOT an error — Host completes the launch macro
 * without invoking Playjector or falling back to a legacy macro — so it is a
 * distinct success variant and MUST NOT be added to `macroBundleErrorSchema`.
 */
export const macroBundleNoMacroResponseSchema = baseMessageSchema.merge(identity).extend({
  type: z.literal(MacroBundleMessageType.RESPONSE), outcome: z.literal('no_macro'),
}).strict();
export const macroBundleErrorResponseSchema = baseMessageSchema.merge(identity).extend({
  type: z.literal(MacroBundleMessageType.RESPONSE), error: macroBundleErrorSchema,
}).strict();
export const macroBundleResponseSchema = z.union([
  macroBundleSuccessResponseSchema, macroBundleNoMacroResponseSchema, macroBundleErrorResponseSchema,
]);
export const macroBundleMessageSchema = z.union([
  macroBundleRequestSchema, macroBundleSuccessResponseSchema, macroBundleNoMacroResponseSchema,
  macroBundleErrorResponseSchema,
]);
export type MacroBundleRequest = z.infer<typeof macroBundleRequestSchema>;
export type MacroBundleNoMacroResponse = z.infer<typeof macroBundleNoMacroResponseSchema>;
export type MacroBundleResponse = z.infer<typeof macroBundleResponseSchema>;
export type MacroBundleMessage = z.infer<typeof macroBundleMessageSchema>;
type Payload<T> = T extends unknown ? Omit<T, 'id' | 'timestamp'> : never;
export function generateMacroBundleMessage(payload: Payload<MacroBundleMessage>): MacroBundleMessage {
  return macroBundleMessageSchema.parse({ id: createMessageId(), timestamp: Date.now(), ...payload });
}
